/**
 * Client-side EHCNA draft sections when AI generation is unavailable or still pending.
 */

export function buildFallbackJourneyNarrative(bundle, studentName) {
  const name = studentName || 'The student';
  const interventions = (bundle?.interventions || [])
    .map((i) => i.interventionName || i.name || 'Intervention')
    .filter(Boolean);
  const provisions = (bundle?.studentProvisions || [])
    .map((p) => p.name)
    .filter(Boolean);
  const sessionLogs = bundle?.sessionLogCount ?? 0;
  const apdrCycles = bundle?.apdrCycleCount ?? 0;
  const reports = bundle?.therapistReports?.length ?? 0;

  const parts = [
    `${name} has been receiving SEN support at school over a sustained period.`,
  ];
  if (interventions.length) {
    parts.push(
      `Pull-out interventions have included: ${interventions.slice(0, 6).join(', ')}.`
    );
  }
  if (provisions.length) {
    parts.push(
      `In-class and pastoral provision includes: ${provisions.slice(0, 6).join(', ')}.`
    );
  }
  if (sessionLogs > 0) {
    parts.push(
      `${sessionLogs} intervention session logs document the graduated response cycle.`
    );
  }
  if (apdrCycles > 0) {
    parts.push(
      `${apdrCycles} completed Assess–Plan–Do–Review cycle(s) of support are on record.`
    );
  }
  if (reports > 0) {
    parts.push(
      `${reports} professional report(s) inform the current picture of need and provision.`
    );
  }
  parts.push(
    'Despite this graduated response, needs remain significant and require consideration for an EHC needs assessment.'
  );
  return parts.join(' ');
}

export function buildFallbackAssessSummary(bundle) {
  const blocks = {
    cognitionLearning: { strengths: '', needs: '' },
    communicationInteraction: { strengths: '', needs: '' },
    semh: { strengths: '', needs: '' },
    sensoryPhysical: { strengths: '', needs: '' },
  };

  (bundle?.studentProvisions || []).forEach((p) => {
    const area = p.areaOfNeed;
    if (!blocks[area]) return;
    const line = p.name + (p.notes ? ` (${p.notes})` : '');
    blocks[area].needs = blocks[area].needs ? `${blocks[area].needs}; ${line}` : line;
  });

  (bundle?.interventions || []).forEach((i) => {
    const label = i.interventionName || i.name;
    if (!label) return;
    blocks.semh.needs = blocks.semh.needs
      ? `${blocks.semh.needs}; ${label} (intervention)`
      : `${label} (intervention)`;
  });

  Object.keys(blocks).forEach((key) => {
    if (!blocks[key].needs) {
      blocks[key].needs = 'Documented through school provision map and graduated response. Review cycle notes for detail.';
    }
    if (!blocks[key].strengths) {
      blocks[key].strengths = 'See session logs, targets, and professional reports in the evidence bundle.';
    }
  });

  return JSON.stringify(blocks);
}

export function buildFallbackSmartOutcomes(bundle) {
  const areas = [
    ['Cognition & Learning', 'cognitionLearning'],
    ['Communication & Interaction', 'communicationInteraction'],
    ['SEMH', 'semh'],
    ['Sensory & Physical', 'sensoryPhysical'],
  ];

  const outcomes = areas.map(([label, areaKey]) => {
    const provisions = (bundle?.studentProvisions || [])
      .filter((p) => p.areaOfNeed === areaKey)
      .map((p) => p.name)
      .filter(Boolean);
    const iWill = provisions.length
      ? `access agreed provision (${provisions.slice(0, 2).join(', ')}) and show measurable progress`
      : 'make sustained progress against agreed SMART targets in this area';
    return {
      area: label,
      byWhen: 'within 24 months',
      iWill,
      soThat: 'their special educational needs are met in this area',
    };
  });

  return JSON.stringify(outcomes);
}
